/**
 * `FloatingPopover` — an anchored popover that escapes the inspector's clipping.
 *
 * The page-builder inspector is a NARROW right rail with `overflow-y-auto`, so a
 * popover positioned `absolute` inside it (the old pattern) is CLIPPED by that
 * scroll box — the symptom the owner hit: the "+ Aggiungi azione" menu (and the
 * resource dropdowns) "opened nothing" because they rendered outside the visible
 * content box. The fix: render the panel into a PORTAL on `document.body` with
 * `position: fixed`, positioned from the anchor's live bounding rect — so it's
 * never clipped by an ancestor's overflow.
 *
 * Default placement is Appsmith-style **`left-center`**: the panel sits to the
 * LEFT of the trigger (into the canvas, where there's room), vertically centered
 * on it; it flips to the right and clamps to the viewport when there's no room.
 * `bottom-start` keeps the classic under-the-input drop (still un-clipped).
 *
 * Outside-click + Escape close it; it repositions on scroll/resize. The anchor's
 * own clicks are ignored so the trigger button keeps owning the open/close toggle.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type PopoverPlacement = "left-center" | "bottom-start";

export function FloatingPopover({
  anchorRef,
  open,
  onClose,
  children,
  className = "",
  placement = "left-center",
  gap = 8,
}: {
  /** The trigger element the panel is positioned against. */
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Visual classes for the panel (bg/border/shadow/size) — NOT positioning. */
  className?: string;
  placement?: PopoverPlacement;
  gap?: number;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Position from the live rects AFTER the panel mounts (so we know its size),
  // and keep it pinned on scroll/resize. Runs in a layout effect → no flicker.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      const p = panelRef.current?.getBoundingClientRect();
      if (!a || !p) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left: number;
      let top: number;
      if (placement === "left-center") {
        left = a.left - gap - p.width;
        top = a.top + a.height / 2 - p.height / 2;
        if (left < 8) left = a.right + gap; // no room on the left → flip right
      } else {
        left = a.left;
        top = a.bottom + gap;
      }
      left = Math.max(8, Math.min(left, vw - p.width - 8));
      top = Math.max(8, Math.min(top, vh - p.height - 8));
      setPos({ left, top });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, anchorRef, placement, gap]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return; // the trigger owns the toggle
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? "visible" : "hidden",
        zIndex: 60,
      }}
      className={className}
    >
      {children}
    </div>,
    document.body,
  );
}
